/**
 * ARCHAT — Módulo API de autenticación
 * Login, registro, reset password, activación, términos, captcha
 */

import { API_ENDPOINTS } from '../utils/constants';
import { generarFingerprintNavegador } from '../utils/helpers';
import { devLog } from '../utils/devConsoleLog';

export const apiAuthMethods = {
  async login(username, password, email = null, captchaData = null) {
    const resolvedEmail = String(email ?? '').trim() || String(username ?? '').trim();
    const body = {
      username: String(username ?? '').trim(),
      email: resolvedEmail,
      password: String(password ?? ''),
    };
    if (captchaData && captchaData.token != null && captchaData.respuesta != null) {
      body.captcha_token = captchaData.token;
      body.captcha_respuesta = captchaData.respuesta;
    }
    try {
      const fingerprint = await generarFingerprintNavegador();
      if (fingerprint) body.fingerprint = fingerprint;
    } catch (_) { /* opcional */ }
    devLog('[LOGIN][PAYLOAD]', { username: body.username, hasPassword: !!body.password, hasCaptcha: !!body.captcha_token });
    return this.request('/api/auth/login.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async googleLogin(idToken) {
    if (!idToken || typeof idToken !== 'string') {
      const err = new Error('id_token required');
      err.code = 'auth.credentials_required';
      throw err;
    }
    return this.request(API_ENDPOINTS.GOOGLE_LOGIN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id_token: idToken.trim() }),
    });
  },

  async acceptTerms(token, version = 'v5_FINAL_ES') {
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return this.request(API_ENDPOINTS.ACCEPT_TERMS, {
      method: 'POST',
      headers,
      body: JSON.stringify({ version: version || 'v5_FINAL_ES' }),
    });
  },

  async getLegalTerms(lang = 'es') {
    const safeLang = typeof lang === 'string' && lang.trim() ? lang.trim().toLowerCase().slice(0, 2) : 'es';
    const url = API_ENDPOINTS.LEGAL_TERMS + '?lang=' + encodeURIComponent(safeLang);
    return this.request(url, { method: 'GET' });
  },

  async getLegalPrivacy(lang = 'es') {
    const safeLang = typeof lang === 'string' && lang.trim() ? lang.trim().toLowerCase().slice(0, 2) : 'es';
    const url = API_ENDPOINTS.LEGAL_PRIVACY + '?lang=' + encodeURIComponent(safeLang);
    return this.request(url, { method: 'GET' });
  },

  async getLegalSubscription(lang = 'es') {
    const safeLang = typeof lang === 'string' && lang.trim() ? lang.trim().toLowerCase().slice(0, 2) : 'es';
    const url = API_ENDPOINTS.LEGAL_SUBSCRIPTION + '?lang=' + encodeURIComponent(safeLang);
    return this.request(url, { method: 'GET' });
  },

  async getLegalRefunds(lang = 'es') {
    const safeLang = typeof lang === 'string' && lang.trim() ? lang.trim().toLowerCase().slice(0, 2) : 'es';
    const url = API_ENDPOINTS.LEGAL_REFUNDS + '?lang=' + encodeURIComponent(safeLang);
    return this.request(url, { method: 'GET' });
  },

  async getLegalCookies(lang = 'es') {
    const safeLang = typeof lang === 'string' && lang.trim() ? lang.trim().toLowerCase().slice(0, 2) : 'es';
    const url = API_ENDPOINTS.LEGAL_COOKIES + '?lang=' + encodeURIComponent(safeLang);
    return this.request(url, { method: 'GET' });
  },

  async activateAccount(activationToken) {
    if (!activationToken || typeof activationToken !== 'string') {
      const err = new Error('activationToken required');
      err.code = 'auth.credentials_required';
      throw err;
    }
    return this.request(API_ENDPOINTS.ACTIVATE_ACCOUNT, {
      method: 'POST',
      headers: { Authorization: `Bearer ${activationToken}` },
      body: JSON.stringify({})
    });
  },

  async completeActivation(email, activationToken) {
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      const err = new Error('Valid email required');
      err.code = 'auth.invalid_data';
      throw err;
    }
    if (!activationToken || typeof activationToken !== 'string') {
      const err = new Error('activationToken required');
      err.code = 'auth.credentials_required';
      throw err;
    }
    return this.request(API_ENDPOINTS.COMPLETE_ACTIVATION, {
      method: 'POST',
      headers: { Authorization: `Bearer ${activationToken}` },
      body: JSON.stringify({ email })
    });
  },

  async register(nick, password, email, captchaData, lang = 'es') {
    const fingerprint = await generarFingerprintNavegador();
    const payload = { nick, password, email, fingerprint, lang };
    if (captchaData && (captchaData.token != null || captchaData.respuesta != null)) {
      payload.captcha_token = captchaData.token ?? '';
      payload.captcha_respuesta = captchaData.respuesta ?? 0;
    }
    return this.request(API_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async resendConfirmation(email) {
    return this.request(API_ENDPOINTS.RESEND_CONFIRMATION, {
      method: 'POST',
      body: JSON.stringify({ email: String(email || '').trim() }),
    });
  },

  async resendActivation(email) {
    return this.request(API_ENDPOINTS.RESEND_ACTIVATION, {
      method: 'POST',
      body: JSON.stringify({ email: String(email || '').trim() }),
      _skipGlobalErrorModal: true,
    });
  },

  async checkUsername(username) {
    const u = encodeURIComponent(String(username ?? '').trim());
    return this.request(`${API_ENDPOINTS.CHECK_USERNAME}?username=${u}`);
  },

  async checkEmail(email) {
    const e = encodeURIComponent(String(email ?? '').trim());
    return this.request(`${API_ENDPOINTS.CHECK_EMAIL}?email=${e}`);
  },

  async generateCaptcha(type = 'login') {
    try {
      const res = await this.request(`${API_ENDPOINTS.GENERATE_CAPTCHA}?type=${type}`);
      devLog('[CAPTCHA_RESPONSE]', res);
      if (res?.csrf_token) {
        localStorage.setItem('archat_csrf_token', res.csrf_token);
      }
      return res;
    } catch (e) {
      devLog('[CAPTCHA_RESPONSE]', e?.data ?? { message: e?.message, code: e?.code });
      throw e;
    }
  },

  async checkAuth(opts = {}) {
    return this.request(API_ENDPOINTS.AUTH_CHECK, { method: 'GET', ...opts });
  },

  async requestPasswordReset(email, captchaData, lang = 'es') {
    const controller = new AbortController();
    const timeoutMs = 12000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const payload = {
        email: String(email ?? '').trim(),
        lang: typeof lang === 'string' && lang.trim() ? lang.trim() : 'es',
      };
      if (captchaData && typeof captchaData === 'object') {
        const tok = captchaData.token;
        const rawAns = captchaData.respuesta;
        const numAns = typeof rawAns === 'number' ? rawAns : parseInt(String(rawAns), 10);
        if (tok != null && tok !== '' && Number.isFinite(numAns)) {
          payload.captcha_token = tok;
          payload.captcha_respuesta = numAns;
        }
      }
      const res = await this.request(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        body: JSON.stringify(payload),
        signal: controller.signal,
        _skipGlobalErrorModal: true,
      });
      clearTimeout(timeoutId);
      return res;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') {
        const e = new Error('No pudimos enviar el email (timeout). Intentá nuevamente.');
        e.code = 'auth.timeout';
        e.status = 408;
        if (typeof window !== 'undefined' && window.__archatLastRequestId) {
          e.request_id = window.__archatLastRequestId;
        }
        throw e;
      }
      throw err;
    }
  },

  async confirmResetPassword(token, newPassword) {
    return this.request(API_ENDPOINTS.CONFIRM_RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ token, password: newPassword }),
      _skipGlobalErrorModal: true,
    });
  },

  async requestDeleteAccount(lang) {
    return this.request(API_ENDPOINTS.REQUEST_DELETE_ACCOUNT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang: lang || 'es' }),
      _skipGlobalErrorModal: true,
    });
  },

  async confirmDeleteAccount(token) {
    return this.request(API_ENDPOINTS.CONFIRM_DELETE_ACCOUNT, {
      method: 'POST',
      body: JSON.stringify({ token }),
      _skipGlobalErrorModal: true,
    });
  },
};
